// /src/components/MapComponent.jsx
import React, { useEffect, useRef, useState } from 'react'
import { MapPin, Navigation, Search, Info } from 'lucide-react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const MapComponent = ({ 
  onCoordinatesChange, 
  initialLat = null, 
  initialLng = null, 
  height = '400px',
  className = '',
  showSearch = true,
  projects = [] // Add projects prop for reforestation projects
}) => {
  const mapRef = useRef(null)
  const mapInstance = useRef(null)
  const markerRef = useRef(null)
  const projectMarkersRef = useRef([])
  const [coordinates, setCoordinates] = useState({
    lat: initialLat || 40.7128,
    lng: initialLng || -74.0060
  })
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [mapError, setMapError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    // Initialize map after component mounts
    const timer = setTimeout(() => {
      initializeMap()
    }, 100)

    return () => {
      clearTimeout(timer)
      // Cleanup
      if (mapInstance.current) {
        mapInstance.current.remove()
      }
    }
  }, [])

  // Update project markers when projects change
  useEffect(() => {
    if (mapInstance.current && projects.length > 0) {
      addProjectMarkers()
    }
  }, [projects])

  const initializeMap = () => {
    try {
      if (!mapRef.current || mapInstance.current) return

      // Default to New York City if no initial coordinates
      const defaultLat = initialLat || 40.7128
      const defaultLng = initialLng || -74.0060

      // Initialize the map
      mapInstance.current = L.map(mapRef.current).setView([defaultLat, defaultLng], 13)

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(mapInstance.current)

      // Add a marker if we have initial coordinates
      if (initialLat && initialLng) {
        markerRef.current = L.marker([initialLat, initialLng]).addTo(mapInstance.current)
      }

      // Handle map clicks to add/update marker
      mapInstance.current.on('click', (e) => {
        const { lat, lng } = e.latlng
        
        // Remove existing marker
        if (markerRef.current) {
          mapInstance.current.removeLayer(markerRef.current)
        }

        // Add new marker
        markerRef.current = L.marker([lat, lng]).addTo(mapInstance.current)
        
        // Update coordinates
        const newCoords = { lat, lng }
        setCoordinates(newCoords)
        onCoordinatesChange(newCoords)
      })

      setIsMapLoaded(true)
      setMapError(null)
    } catch (error) {
      console.error('Map initialization error:', error)
      setMapError('Failed to initialize map')
    }
  }

  const addProjectMarkers = () => {
    // Clear existing project markers
    projectMarkersRef.current.forEach(marker => {
      if (marker && mapInstance.current) {
        mapInstance.current.removeLayer(marker)
      }
    })
    projectMarkersRef.current = []

    // Add markers for each project
    projects.forEach(project => {
      if (project.coordinates && project.coordinates.lat && project.coordinates.lng) {
        // Create custom icon for project markers
        const projectIcon = L.divIcon({
          className: 'custom-project-marker',
          html: `
            <div style="
              background: linear-gradient(135deg, #10b981, #059669);
              width: 24px;
              height: 24px;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 12px;
            ">
              🌱
            </div>
          `,
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        })

        const marker = L.marker([project.coordinates.lat, project.coordinates.lng], {
          icon: projectIcon
        }).addTo(mapInstance.current)

        // Add popup with project info
        const popupContent = `
          <div style="min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; color: #065f46; font-weight: bold;">${project.name}</h3>
            <p style="margin: 0 0 8px 0; color: #374151; font-size: 14px;">${project.location}</p>
            <div style="display: flex; justify-content: space-between; margin: 8px 0;">
              <span style="color: #059669; font-weight: bold;">${project.treesPlanted.toLocaleString()} trees</span>
              <span style="color: #6b7280; font-size: 12px;">${project.status}</span>
            </div>
            <p style="margin: 8px 0 0 0; color: #6b7280; font-size: 12px; line-height: 1.4;">${project.description}</p>
          </div>
        `
        marker.bindPopup(popupContent)

        projectMarkersRef.current.push(marker)
      }
    })

    // Fit map to show all markers if there are projects
    if (projects.length > 0) {
      const bounds = L.latLngBounds(projects.map(p => [p.coordinates.lat, p.coordinates.lng]))
      mapInstance.current.fitBounds(bounds, { padding: [20, 20] })
    }
  }

  const handleSearch = async () => {
    console.log('🔍 Starting search for:', searchQuery);
    if (!searchQuery.trim()) return

    try {
      // Use Nominatim API for geocoding (reverted to working version)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`
      )
      const data = await response.json()

      console.log('🔍 Search results:', data);

      if (data.length > 0) {
        const { lat, lon } = data[0]
        const newCoords = { lat: parseFloat(lat), lng: parseFloat(lon) }
        
        console.log('🔍 Found location:', newCoords);
        
        // Update map view
        mapInstance.current.setView([lat, lon], 13)
        
        // Update marker
        if (markerRef.current) {
          mapInstance.current.removeLayer(markerRef.current)
        }
        markerRef.current = L.marker([lat, lon]).addTo(mapInstance.current)
        
        // Update coordinates state
        setCoordinates(newCoords)
        
        // Only call onCoordinatesChange if the coordinates actually changed significantly
        const currentLat = coordinates.lat
        const currentLng = coordinates.lng
        const latDiff = Math.abs(currentLat - parseFloat(lat))
        const lngDiff = Math.abs(currentLng - parseFloat(lon))
        
        // Only update if change is more than 0.0001 degrees (roughly 11 meters)
        if (latDiff > 0.0001 || lngDiff > 0.0001) {
          console.log('🔍 Updating coordinates via onCoordinatesChange');
          onCoordinatesChange(newCoords)
        }
      } else {
        console.log('🔍 No search results found - keeping current coordinates');
      }
    } catch (error) {
      console.error('Geocoding error:', error)
    }
  }

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          const newCoords = { lat: latitude, lng: longitude }
          
          // Update map view
          mapInstance.current.setView([latitude, longitude], 13)
          
          // Update marker
          if (markerRef.current) {
            mapInstance.current.removeLayer(markerRef.current)
          }
          markerRef.current = L.marker([latitude, longitude]).addTo(mapInstance.current)
          
          // Update coordinates state
          setCoordinates(newCoords)
          
          // Only call onCoordinatesChange if the coordinates actually changed significantly
          const currentLat = coordinates.lat
          const currentLng = coordinates.lng
          const latDiff = Math.abs(currentLat - latitude)
          const lngDiff = Math.abs(currentLng - longitude)
          
          // Only update if change is more than 0.0001 degrees (roughly 11 meters)
          if (latDiff > 0.0001 || lngDiff > 0.0001) {
            onCoordinatesChange(newCoords)
          }
        },
        (error) => {
          console.error('Geolocation error:', error)
        }
      )
    }
  }

  // Add effect to sync coordinates when they change externally (like manual input)
  useEffect(() => {
    if (initialLat && initialLng && mapInstance.current) {
      const newCoords = { lat: initialLat, lng: initialLng }
      setCoordinates(newCoords)
      
      // Update map view and marker
      mapInstance.current.setView([initialLat, initialLng], 13)
      
      if (markerRef.current) {
        mapInstance.current.removeLayer(markerRef.current)
      }
      markerRef.current = L.marker([initialLat, initialLng]).addTo(mapInstance.current)
    }
  }, [initialLat, initialLng])



  return (
    <div className={`map-container ${className}`}>
      {showSearch && (
        <div className="mb-4 space-y-2">
          <div className="flex space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-forest-400" />
              <input
                type="text"
                placeholder="Search for a location..."
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  console.log('🔍 Search input changed:', value);
                  setSearchQuery(value);
                }}
                className="w-full pl-10 pr-3 py-2 border border-forest-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    console.log('🔍 Search Enter pressed');
                    e.preventDefault(); // Prevent form submission
                    handleSearch();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission
                  }
                }}
              />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => getCurrentLocation()}
              className="px-4 py-2 bg-forest-600 text-white rounded-md hover:bg-forest-700 transition-colors"
              title="Use current location"
            >
              <Navigation className="w-4 h-4" />
            </button>
          </div>
          {projects.length > 0 && (
            <div className="flex items-center space-x-2 text-sm text-forest-600">
              <Info className="w-4 h-4" />
              <span>{projects.length} reforestation project{projects.length !== 1 ? 's' : ''} shown on map</span>
            </div>
          )}
        </div>
      )}
      
      {mapError ? (
        <div className="text-red-600 text-sm p-4 border border-red-200 rounded-lg">
          <p className="mb-2">{mapError}</p>
          <button 
            onClick={() => {
              setMapError(null)
              initializeMap()
            }}
            className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
          >
            Retry
          </button>
        </div>
      ) : (
        <div 
          ref={mapRef} 
          style={{ height }} 
          className="rounded-lg border border-forest-300"
        />
      )}
      
      {isMapLoaded && !mapError && (
        <div className="mt-2 text-sm text-forest-600">
          <MapPin className="w-4 h-4 inline mr-1" />
          Coordinates: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
        </div>
      )}
    </div>
  )
}

export default MapComponent 